import React, { useState } from 'react';

const AdminManagement = ({
  admins,
  currentUser,
  entities,  // Lista de entidades disponíveis
  onCreateAdmin,
  onUpdateAdmin,
  onDeleteAdmin,
  setIsEditing
}) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [formData, setFormData] = useState({});

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (ativo) => {
    return (
      <span className={`badge ${ativo ? 'approved' : 'suspended'}`}>
        {ativo ? 'Ativo' : 'Inativo'}
      </span>
    );
  };

  const getAdminTypeBadge = (adminType) => {
    const typeMap = {
      'mestre': { class: 'primary', text: 'Admin Mestre' },
      'entidade': { class: 'secondary', text: 'Admin Entidades' }
    };
    
    const typeInfo = typeMap[adminType] || { class: 'secondary', text: adminType };
    
    return (
      <span className={`badge ${typeInfo.class}`}>
        {typeInfo.text}
      </span>
    );
  };

  const handleCreateAdmin = () => {
    setFormData({
      email: '',
      nome: '',
      telefone: '',
      tipo: 'entidade',
      entidades: []  // Array de IDs de entidades
    });
    setShowCreateModal(true);
    setIsEditing(true);
  };

  const handleEditAdmin = (admin) => {
    setSelectedAdmin(admin);

    // Extract entity IDs from the entidades array (which contains objects with id and nome)
    // Ensure they are strings for proper comparison with option values
    const entityIds = admin.entidades && admin.entidades.length > 0
      ? admin.entidades.map(ent => String(ent.id))
      : [];

    setFormData({
      nome: admin.nome || '',
      telefone: admin.telefone || '',
      tipo: admin.tipo,
      entidades: entityIds,  // Array de IDs de entidades (strings)
      ativo: admin.ativo
    });
    setShowEditModal(true);
    setIsEditing(true);
  };

  const handleDeleteAdmin = async (admin) => {
    const confirmed = window.confirm(
      `Tem a certeza que deseja ELIMINAR PERMANENTEMENTE o administrador "${admin.nome || admin.email}"?\n\n` +
      `⚠️ ATENÇÃO: Esta ação é irreversível!\n` +
      `- O administrador será removido definitivamente\n` +
      `- Todas as associações com entidades serão eliminadas\n\n` +
      `Nota: Se este administrador tiver aprovado voluntários, a eliminação será bloqueada.`
    );

    if (confirmed) {
      try {
        await onDeleteAdmin(admin.id);
      } catch (error) {
        alert('Erro ao eliminar administrador: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  const submitCreateAdmin = async () => {
    if (!formData.email || !formData.tipo) {
      alert('Email e tipo de administrador são obrigatórios');
      return;
    }

    if (formData.tipo === 'entidade' && (!formData.entidades || formData.entidades.length === 0)) {
      alert('Pelo menos uma entidade é obrigatória para administradores de entidade');
      return;
    }

    try {
      await onCreateAdmin(formData);
      setShowCreateModal(false);
      setFormData({});
      setIsEditing(false);
    } catch (error) {
      alert('Erro ao criar administrador: ' + (error.response?.data?.message || error.message));
    }
  };

  const submitEditAdmin = async () => {
    try {
      // Prepare data to send - only send entidades for 'entidade' type admins
      const dataToSend = {
        nome: formData.nome,
        telefone: formData.telefone,
        tipo: formData.tipo,
        ativo: formData.ativo
      };

      // Only include entidades if admin type is 'entidade'
      if (formData.tipo === 'entidade') {
        dataToSend.entidades = formData.entidades || [];
      }

      await onUpdateAdmin(selectedAdmin.id, dataToSend);
      setShowEditModal(false);
      setSelectedAdmin(null);
      setFormData({});
      setIsEditing(false);
    } catch (error) {
      alert('Erro ao atualizar administrador: ' + (error.response?.data?.message || error.message));
    }
  };

  const closeModals = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setSelectedAdmin(null);
    setFormData({});
    setIsEditing(false);
  };

  // Usar entidades disponíveis do backend
  const availableEntities = entities || [];

  return (
    <div className="admin-management">
      <div className="card-header">
        <h3>Gestão de Administradores</h3>
        <button className="btn btn-primary" onClick={handleCreateAdmin}>
          Novo Administrador
        </button>
      </div>

      <div className="card-content">
        {admins?.length === 0 ? (
          <div className="empty-state">
            <p>Nenhum administrador encontrado</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Nome</th>
                  <th>Telefone</th>
                  <th>Tipo</th>
                  <th>Organização</th>
                  <th>Status</th>
                  <th>Último Login</th>
                  <th>Criado</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {admins?.map(admin => (
                  <tr key={admin.id}>
                    <td>
                      <strong>{admin.email}</strong>
                      {admin.id === currentUser?.id && (
                        <small className="text-muted d-block">(Você)</small>
                      )}
                    </td>
                    <td>{admin.nome || <em className="text-muted">Nome não definido</em>}</td>
                    <td>{admin.telefone || <em className="text-muted">-</em>}</td>
                    <td>{getAdminTypeBadge(admin.tipo)}</td>
                    <td>
                      {admin.entidades && admin.entidades.length > 0 ? (
                        <div>
                          {admin.entidades.map((ent, idx) => (
                            <div key={idx}>
                              <strong>{ent.id}</strong> - <small>{ent.nome}</small>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted">N/A</span>
                      )}
                    </td>
                    <td>{getStatusBadge(admin.ativo)}</td>
                    <td>
                      {admin.last_login ? (
                        <span>
                          {formatDate(admin.last_login)}
                        </span>
                      ) : (
                        <span className="text-muted">Nunca</span>
                      )}
                    </td>
                    <td>{formatDate(admin.created_at)}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleEditAdmin(admin)}
                          title="Editar"
                        >
                          ✏️
                        </button>
                        {admin.id !== currentUser?.id && (
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDeleteAdmin(admin)}
                            title="Eliminar"
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Admin Modal */}
      {showCreateModal && (
        <div className="modal">
          <div className="modal-content">
            <h4>Novo Administrador</h4>
            <form onSubmit={(e) => { e.preventDefault(); submitCreateAdmin(); }}>
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="admin@example.com"
                  required
                />
                <small className="form-text">
                  Este email deve ter acesso ao Google OAuth para fazer login
                </small>
              </div>
              <div className="form-group">
                <label>Nome (Opcional)</label>
                <input
                  type="text"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  placeholder="Nome completo (será preenchido no primeiro login)"
                />
              </div>
              <div className="form-group">
                <label>Telefone (Opcional)</label>
                <input
                  type="tel"
                  value={formData.telefone}
                  onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                  placeholder="+351 912 345 678"
                />
              </div>
              <div className="form-group">
                <label>Tipo de Administrador *</label>
                <select
                  value={formData.tipo}
                  onChange={(e) => {
                    const newType = e.target.value;
                    setFormData({
                      ...formData,
                      tipo: newType,
                      entidades: newType === 'mestre' ? [] : formData.entidades
                    });
                  }}
                  required
                >
                  <option value="entidade">Admin Entidades</option>
                  <option value="mestre">Admin Mestre</option>
                </select>
              </div>
              {formData.tipo === 'entidade' && (
                <div className="form-group">
                  <label>Entidades * (pode selecionar múltiplas)</label>
                  <select
                    multiple
                    value={formData.entidades || []}
                    onChange={(e) => {
                      const selected = Array.from(e.target.selectedOptions, option => option.value);
                      setFormData({ ...formData, entidades: selected });
                    }}
                    style={{ minHeight: '100px' }}
                    required
                  >
                    {availableEntities.map(entity => (
                      <option key={entity.id} value={entity.id}>
                        {entity.id} - {entity.nome}
                      </option>
                    ))}
                  </select>
                  <small className="form-text">
                    Mantenha Ctrl (ou Cmd no Mac) pressionado para selecionar múltiplas entidades
                  </small>
                </div>
              )}
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={closeModals}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  Criar Administrador
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Admin Modal */}
      {showEditModal && (
        <div className="modal">
          <div className="modal-content">
            <h4>Editar Administrador: {selectedAdmin?.email}</h4>
            <form onSubmit={(e) => { e.preventDefault(); submitEditAdmin(); }}>
              <div className="form-group">
                <label>Nome</label>
                <input
                  type="text"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  placeholder="Nome completo"
                />
              </div>
              <div className="form-group">
                <label>Telefone</label>
                <input
                  type="tel"
                  value={formData.telefone}
                  onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                  placeholder="+351 912 345 678"
                />
              </div>
              <div className="form-group">
                <label>Tipo de Administrador</label>
                <select
                  value={formData.tipo}
                  onChange={(e) => {
                    const newType = e.target.value;
                    setFormData({
                      ...formData,
                      tipo: newType,
                      entidades: newType === 'mestre' ? [] : formData.entidades
                    });
                  }}
                  disabled={selectedAdmin?.id === currentUser?.id}
                >
                  <option value="entidade">Admin Entidades</option>
                  <option value="mestre">Admin Mestre</option>
                </select>
                {selectedAdmin?.id === currentUser?.id && (
                  <small className="form-text">
                    Não pode alterar o seu próprio tipo de administrador
                  </small>
                )}
              </div>
              {formData.tipo === 'entidade' && (
                <div className="form-group">
                  <label>Entidades (pode selecionar múltiplas)</label>
                  <select
                    multiple
                    value={formData.entidades || []}
                    onChange={(e) => {
                      const selected = Array.from(e.target.selectedOptions, option => option.value);
                      setFormData({ ...formData, entidades: selected });
                    }}
                    style={{ minHeight: '100px' }}
                  >
                    {availableEntities.map(entity => (
                      <option key={entity.id} value={entity.id}>
                        {entity.id} - {entity.nome}
                      </option>
                    ))}
                  </select>
                  <small className="form-text">
                    Mantenha Ctrl (ou Cmd no Mac) pressionado para selecionar múltiplas entidades
                  </small>
                </div>
              )}
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.ativo}
                    onChange={(e) => setFormData({ ...formData, ativo: e.target.checked })}
                    disabled={selectedAdmin?.id === currentUser?.id}
                  />
                  {' '}Administrador ativo
                </label>
                {selectedAdmin?.id === currentUser?.id && (
                  <small className="form-text">
                    Não pode desativar a sua própria conta
                  </small>
                )}
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={closeModals}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  Atualizar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminManagement;